import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { BranchProvider } from '@/contexts/branches/BranchContext'
import { OrderProvider } from '@/contexts/orders/OrderContext'
import { CompanySettingsProvider } from '@/contexts/settings/CompanySettingsContext'
import { WorkflowProvider } from '@/contexts/workflow/WorkflowContext'
import { CustomerProvider } from '@/contexts/customers/CustomerContext'
import { MeasurementsProvider } from '@/contexts/measurements/MeasurementsContext'

// Layout
const Layout = lazy(() => import('@/components/layout/Layout'))

// Pages
const Login = lazy(() => import('@/pages/auth/Login'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Customers = lazy(() => import('@/pages/customers/Customers'))
const CustomerDetails = lazy(() => import('@/pages/customers/CustomerDetails'))
const EditCustomer = lazy(() => import('@/pages/customers/EditCustomer'))
const AddNote = lazy(() => import('@/pages/customers/AddNote'))
const EditNote = lazy(() => import('@/pages/customers/EditNote'))
const ViewAllMeasurements = lazy(() => import('@/pages/customers/ViewAllMeasurements'))
const EditMeasurement = lazy(() => import('@/pages/customers/EditMeasurement'))
const OrderCreation = lazy(() => import('@/pages/orders/OrderCreation'))
const Measurements = lazy(() => import('@/pages/measurements/Measurements'))
const AddCustomer = lazy(() => import('@/pages/customers/AddCustomer'))

// Employee Pages
const Employees = lazy(() => import('@/pages/employees/Employees'))
const AddEmployee = lazy(() => import('@/pages/employees/AddEmployee'))
const EmployeeDetails = lazy(() => import('@/pages/employees/EmployeeDetails'))
const EditEmployee = lazy(() => import('@/pages/employees/EditEmployee'))

// Branch Pages
const Branches = lazy(() => import('@/pages/branches/Branches'))
const AddBranch = lazy(() => import('@/pages/branches/AddBranch'))
const BranchDetails = lazy(() => import('@/pages/branches/BranchDetails'))
const EditBranch = lazy(() => import('@/pages/branches/EditBranch'))

// Order Pages
const Orders = lazy(() => import('@/pages/orders/Orders'))
const AddMeasurement = lazy(() => import('@/pages/orders/AddMeasurement'))
const EditOrder = lazy(() => import('@/pages/orders/EditOrder'))
const PrintOrderSummary = lazy(() => import('@/pages/orders/PrintOrderSummary'))

// Settings Pages
const Settings = lazy(() => import('@/pages/settings/Settings'))

// Workflow Pages
const Workflows = lazy(() => import('@/pages/workflow/Workflows'))
const OrderAssignment = lazy(() => import('@/pages/workflow/OrderAssignment'))
const MonthlyReport = lazy(() => import('@/pages/workflow/MonthlyReport'))

// Reports Pages
const Reports = lazy(() => import('@/pages/reports/Reports'))
const PaymentsReport = lazy(() => import('@/pages/reports/PaymentsReport'))
const WorkflowReport = lazy(() => import('@/pages/reports/WorkflowReport'))
const EmployeeReport = lazy(() => import('@/pages/reports/EmployeeReport'))

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CompanySettingsProvider>
          <BranchProvider>
            <OrderProvider>
              <CustomerProvider>
                <MeasurementsProvider>
                  <WorkflowProvider>
              <ErrorBoundary>
              <Suspense fallback={<div>Loading...</div>}>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route
                  path="/orders/:orderId/print"
                  element={
                    <ProtectedRoute>
                      <PrintOrderSummary />
                    </ProtectedRoute>
                  }
                />
                <Route path="/*" element={<Layout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="customers" element={<Customers />} />
                  <Route path="customers/:customerId" element={<CustomerDetails />} />
                  <Route path="customers/:customerId/edit" element={<EditCustomer />} />
                  <Route path="customers/:customerId/notes/add" element={<AddNote />} />
                  <Route path="customers/:customerId/notes/:noteId/edit" element={<EditNote />} />
                  <Route path="customers/:customerId/measurements/:measurementType" element={<ViewAllMeasurements />} />
                  <Route path="customers/:customerId/measurements/:measurementType/edit" element={<EditMeasurement />} />
                  <Route path="customers/add" element={<AddCustomer />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/create" element={<OrderCreation />} />
                  <Route path="orders/new" element={<OrderCreation />} />
                  <Route path="orders/measurements/add/:customerId/:garmentType" element={<AddMeasurement />} />
                  <Route path="orders/:orderId" element={<Navigate to="edit" replace />} />
                  <Route path="orders/:orderId/edit" element={<EditOrder />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="measurements" element={<Measurements />} />
                  <Route path="employees" element={<Employees />} />
                  <Route path="employees/add" element={<AddEmployee />} />
                  <Route path="employees/:id" element={<EmployeeDetails />} />
                  <Route path="employees/:id/edit" element={<EditEmployee />} />
                  <Route path="branches" element={<Branches />} />
                  <Route path="branches/add" element={<AddBranch />} />
                  <Route path="branches/:id" element={<BranchDetails />} />
                  <Route path="branches/:id/edit" element={<EditBranch />} />
                  
                  {/* Workflow Routes */}
                  <Route path="workflows" element={<Workflows />} />
                  <Route path="workflows/order/:orderId" element={<OrderAssignment />} />
                  <Route path="workflows/reports" element={<MonthlyReport />} />
                  
                  {/* Reports Routes */}
                  <Route path="reports" element={<Reports />} />
                  <Route path="reports/payments" element={<PaymentsReport />} />
                  <Route path="reports/workflow" element={<WorkflowReport />} />
                  <Route path="reports/employee" element={<EmployeeReport />} />
                </Route>
              </Routes>
            </Suspense>
            <Toaster />
                      </ErrorBoundary>
                  </WorkflowProvider>
                </MeasurementsProvider>
              </CustomerProvider>
            </OrderProvider>
          </BranchProvider>
        </CompanySettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App 
